/* Product spotlight carousel — arrow navigation for the
   scroll-snap track. Touch/momentum/snap are native; this
   element wires the prev/next buttons, their disabled state,
   and ArrowLeft/ArrowRight on the focusable track (native
   per-pixel arrow scrolling fights mandatory snap in some
   engines). On mobile it also renders pagination dots; an
   optional auto-rotate runs at all widths (alongside the
   desktop arrows). Pairs with section-product-spotlight.css. */

if (!customElements.get("product-spotlight-carousel")) {
  customElements.define(
    "product-spotlight-carousel",
    class ProductSpotlightCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector(".psc__track");
        this.prevBtn = this.querySelector(".psc__nav--prev");
        this.nextBtn = this.querySelector(".psc__nav--next");
        if (!this.track) return;

        this.shuffleFill();

        this.onScroll = this.onScroll.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPrev = () => this.scrollByCard(-1);
        this.onNext = () => this.scrollByCard(1);
        /* Re-init on every (re)connect: a frame cancelled during
           disconnect would otherwise leave a stale id that wedges
           the throttle for the element's remaining lifetime. */
        this.rafId = null;

        this.prevBtn?.addEventListener("click", this.onPrev);
        this.nextBtn?.addEventListener("click", this.onNext);
        this.track.addEventListener("scroll", this.onScroll, { passive: true });
        this.track.addEventListener("keydown", this.onKeydown);
        window.addEventListener("resize", this.onScroll, { passive: true });

        this.buildDots();
        this.setupAutoplay();

        this.onScroll();
      }

      /* Collection mode renders a larger pool of cards than the board shows.
         data-psc-show="N" is the target visible count. We Fisher–Yates shuffle
         only the collection-fill cards (.psc__card--fill) and keep just enough
         to bring the board to N, leaving pinned card-block cards fixed and
         first. Runs before quick-add.js upgrades the cards (defer scripts
         execute in document order, and this script is listed first), so the
         heavy quick-add bundle only initializes on the survivors. No-JS
         fallback: the whole pool renders un-shuffled — more products, not
         broken. Trimming cards shortens the horizontal scroll width only, so
         there is no vertical layout shift. */
      shuffleFill() {
        const show = parseInt(this.track.dataset.pscShow, 10);
        if (!Number.isFinite(show)) return;

        const fill = Array.from(
          this.track.querySelectorAll(":scope > .psc__card--fill"),
        );
        if (fill.length === 0) return;

        const pinned = this.track.querySelectorAll(
          ":scope > .psc__card:not(.psc__card--fill):not(.psc__card--empty)",
        ).length;
        const keep = Math.max(0, show - pinned);

        for (let i = fill.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [fill[i], fill[j]] = [fill[j], fill[i]];
        }

        fill.forEach((card, index) => {
          if (index < keep) {
            this.track.appendChild(card); // re-append after pins, in shuffled order
          } else {
            card.remove(); // drop the surplus pool
          }
        });
      }

      /* Mobile pagination dots — one per rendered card, injected below the
         track AFTER shuffleFill so the count matches the trimmed board.
         Hidden at desktop widths via CSS (the arrows take over there).
         Progressive enhancement: with no JS the native swipe still works,
         there's just no indicator. Skipped for a single card. */
      buildDots() {
        // Drop any dots from a previous connect before rebuilding.
        this.querySelector(":scope > .psc__dots")?.remove();
        this.dots = [];
        this.activeIndex = -1;

        this.cards = Array.from(
          this.track.querySelectorAll(
            ":scope > .psc__card:not(.psc__card--empty)",
          ),
        );
        if (this.cards.length < 2) return;

        const dotsEl = document.createElement("div");
        dotsEl.className = "psc__dots";

        this.dots = this.cards.map((card, i) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = "psc__dot";
          dot.setAttribute("aria-label", `Go to product ${i + 1}`);
          dot.addEventListener("click", () => this.scrollToIndex(i));
          dotsEl.appendChild(dot);
          return dot;
        });

        this.appendChild(dotsEl);
        this.updateActiveDot();
      }

      disconnectedCallback() {
        this.prevBtn?.removeEventListener("click", this.onPrev);
        this.nextBtn?.removeEventListener("click", this.onNext);
        this.track?.removeEventListener("scroll", this.onScroll);
        this.track?.removeEventListener("keydown", this.onKeydown);
        window.removeEventListener("resize", this.onScroll);
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = null;

        this.stopAutoplay();
        if (this.onVisibility) {
          document.removeEventListener("visibilitychange", this.onVisibility);
        }
        if (this.onMediaChange) {
          this.mobileMQ?.removeEventListener("change", this.onMediaChange);
          this.reducedMQ?.removeEventListener("change", this.onMediaChange);
        }
        this.io?.disconnect();
      }

      cardStep() {
        const card = this.track.querySelector(".psc__card");
        if (!card) return this.track.clientWidth;
        const styles = getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      scrollByCard(direction) {
        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
          .matches
          ? "auto"
          : "smooth";
        this.track.scrollBy({ left: direction * this.cardStep(), behavior });
      }

      /* Absolute scroll to a card index. Cards are a uniform width on each
         breakpoint, so the rest position of card i is i × cardStep. */
      scrollToIndex(index) {
        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
          .matches
          ? "auto"
          : "smooth";
        this.track.scrollTo({ left: index * this.cardStep(), behavior });
      }

      onKeydown(event) {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        this.scrollByCard(event.key === "ArrowLeft" ? -1 : 1);
      }

      onScroll() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
          if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 1;
          if (this.nextBtn)
            this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
          this.updateActiveDot();
        });
      }

      /* Light up the dot for the card nearest the scroll position. Forced to
         the last dot at the scroll end, where the trailing edge gutter can
         leave the final card resting a few px short of index × cardStep. */
      updateActiveDot() {
        if (!this.dots || this.dots.length === 0) return;
        const step = this.cardStep();
        let index = step > 0 ? Math.round(this.track.scrollLeft / step) : 0;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
        if (this.track.scrollLeft >= maxScroll) index = this.dots.length - 1;
        index = Math.max(0, Math.min(index, this.dots.length - 1));
        if (index === this.activeIndex) return;
        this.activeIndex = index;
        this.dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle("is-active", active);
          if (active) dot.setAttribute("aria-current", "true");
          else dot.removeAttribute("aria-current");
        });
      }

      /* Auto-rotate at all widths (mobile + desktop). Reads data-autoplay /
         data-autoplay-interval (set from the section settings). Advances one
         card every N seconds and rewinds at the end. Pauses when the section is
         off-screen or the tab is hidden, and is disabled under reduced-motion.
         On desktop it runs alongside the prev/next arrows. Mirrors the "Shop by
         needs" Swiper autoplay: keeps rotating after a manual swipe/arrow click
         (disableOnInteraction:false). */
      setupAutoplay() {
        this.autoplayTimer = null;
        if (this.dataset.autoplay !== "true") return;
        if (!this.dots || this.dots.length < 2) return;

        const seconds = parseFloat(this.dataset.autoplayInterval) || 4;
        this.autoplayDelay = seconds * 1000;
        this.mobileMQ = window.matchMedia("(max-width: 899.98px)");
        this.reducedMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.isVisible = document.visibilityState !== "hidden";
        this.isOnscreen = true;

        this.onVisibility = () => {
          this.isVisible = document.visibilityState !== "hidden";
          this.refreshAutoplay();
        };
        this.onMediaChange = () => this.refreshAutoplay();

        document.addEventListener("visibilitychange", this.onVisibility);
        this.mobileMQ.addEventListener("change", this.onMediaChange);
        this.reducedMQ.addEventListener("change", this.onMediaChange);

        if ("IntersectionObserver" in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              this.isOnscreen = entries[0].isIntersecting;
              this.refreshAutoplay();
            },
            { threshold: 0.2 },
          );
          this.io.observe(this);
        }

        this.refreshAutoplay();
      }

      autoplayShouldRun() {
        /* Runs at ALL widths now (mobile + desktop). The old mobile-only gate
           (this.mobileMQ?.matches) was intentionally removed so the section
           auto-rotates on desktop too, alongside the prev/next arrows. Still
           pauses when the tab is hidden or the section is off-screen, and stays
           disabled under reduced-motion. */
        return (
          this.dataset.autoplay === "true" &&
          !this.reducedMQ?.matches &&
          this.isVisible &&
          this.isOnscreen
        );
      }

      refreshAutoplay() {
        if (this.autoplayShouldRun()) this.startAutoplay();
        else this.stopAutoplay();
      }

      startAutoplay() {
        if (this.autoplayTimer) return;
        this.autoplayTimer = setInterval(
          () => this.autoplayTick(),
          this.autoplayDelay,
        );
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      autoplayTick() {
        const count = this.dots.length;
        const current = this.activeIndex < 0 ? 0 : this.activeIndex;
        const next = current + 1 >= count ? 0 : current + 1;
        this.scrollToIndex(next);
      }
    },
  );
}
